import { createTheme } from "@mui/material";
import { FiChevronDown } from "react-icons/fi";
import { tokens } from "./tokens";
import { fluidTypography } from "./utils/responsive";

export const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 1000,
      lg: 1200,
      xl: 1920,
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow:
            "0px 23px 44px 0 rgba(176, 183, 195, 0.22), 0 1px 3px 0 rgba(0, 0, 0, 0.15)",
        },
      },
    },
    MuiChip: {
      variants: [
        {
          props: { variant: "stats-up" },
          style: {
            backgroundColor: tokens.chip.green.bg,
            color: tokens.chip.green.text,
            fontSize: tokens.fontSize.base,
            padding: "0 4px",
            "& .MuiChip-label": {
              color: tokens.chip.green.text,
              fontSize: tokens.fontSize.base,
              fontWeight: tokens.fontWeight.semibold,
            },
            "& .MuiChip-icon": {
              fontSize: tokens.fontSize.lg,
              color: tokens.chip.green.text,
            },
          },
        },
      ],
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      variants: [
        {
          props: {
            variant: "white-contained",
          },
          style: {
            backgroundColor: tokens.bg.surface,
            borderRadius: tokens.radius.sm,
            boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.12)",
            color: tokens.text.primary,
            padding: "12px 1rem",
            minHeight: 46,
            "&: hover": {
              backgroundColor: tokens.bg.surface,
              boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.12)",
            },
            "&:focus": {
              backgroundColor: tokens.bg.surface,
              boxShadow:
                `0 0 0 4px ${tokens.brand.accentFocus}, 0 1px 2px 0 rgba(78, 78, 78, 0.5)`,
            },
          },
        },
        {
          props: {
            variant: "primary-contained",
          },
          style: {
            backgroundColor: tokens.brand.primary,
            borderRadius: tokens.radius.sm,
            boxShadow: "0 1px 2px 0 #1018280D",
            color: "white",
            padding: "12px 1rem",
            minHeight: 46,
            "&:hover": {
              backgroundColor: tokens.brand.primaryHover,
              boxShadow: "0 1px 2px 0 #1018280D",
            },
            "&:focus": {
              backgroundColor: tokens.brand.primary,
              boxShadow:
                `0 0 0 4px ${tokens.brand.accentFocus}, 0 1px 2px 0 rgba(78, 78, 78, 0.5)`,
            },
            "&.Mui-disabled": {
              color: tokens.bg.surface,
            },
          },
        },
        {
          props: {
            variant: "white-outlined",
          },
          style: {
            backgroundColor: tokens.bg.surface,
            borderRadius: tokens.radius.sm,
            boxShadow: "0 1px 2px 0 #1018280D",
            padding: "12px 1rem",
            border: `1px solid ${tokens.border.default}`,
            color: tokens.text.primary,
            fontSize: tokens.fontSize.base,
            fontWeight: tokens.fontWeight.semibold,
            "&: hover": {
              boxShadow: "0 1px 2px 0 #1018280D",
            },
            "&:focus": {
              boxShadow:
                `0 0 0 4px ${tokens.brand.accentFocus}, 0 1px 2px 0 rgba(78, 78, 78, 0.5)`,
            },
          },
        },
      ],
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: tokens.radius.sm,
        },
        contained: {
          boxShadow: "none",
        },
        outlined: {
          border: `1px solid ${tokens.border.default}`,
          boxShadow: "unset",
          borderRadius: "32px",
          padding: "3px 20px",
          width: "fit-content",
          backgroundColor: "transparent",
          borderColor: "unset",
        },
        sizeSmall: {
          padding: "6px 16px",
        },
        sizeMedium: {
          padding: "8px 20px",
        },
        sizeLarge: {
          padding: "9px 21px",
        },
        textSizeSmall: {
          padding: "7px 12px",
        },
        textSizeMedium: {
          padding: "9px 16px",
        },
        textSizeLarge: {
          padding: "12px 16px",
        },
        outlinedPrimary: {
          borderColor: tokens.text.primary,
        },
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: false,
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: "32px 24px",
          "&:last-child": {
            paddingBottom: "32px",
          },
        },
      },
    },
    MuiCardHeader: {
      defaultProps: {
        titleTypographyProps: {
          variant: "h6",
        },
        subheaderTypographyProps: {
          variant: "body2",
        },
      },
      styleOverrides: {
        root: {
          padding: "32px 24px",
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        "*": {
          boxSizing: "border-box",
          margin: 0,
          padding: 0,
        },
        html: {
          MozOsxFontSmoothing: "grayscale",
          WebkitFontSmoothing: "antialiased",
          display: "flex",
          flexDirection: "column",
          minHeight: "100%",
          width: "100%",
        },
        body: {
          display: "flex",
          flex: "1 1 auto",
          flexDirection: "column",
          minHeight: "100%",
          width: "100%",
          backgroundColor: tokens.bg.muted,
        },
        "#__next": {
          display: "flex",
          flex: "1 1 auto",
          flexDirection: "column",
          height: "100%",
          width: "100%",
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: tokens.text.muted,
          "&.Mui-selected": {
            color: tokens.brand.accent,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        input: {
          padding: "11.5px 14px",
          "&::placeholder": {
            color: "#aeb3b3",
            letterSpacing: "-0.01em",
            fontSize: tokens.fontSize.xl,
            fontWeight: tokens.fontWeight.normal,
            opacity: 1,
          },
        },
        root: {
          fontSize: tokens.fontSize.xl,
          fontWeight: tokens.fontWeight.semibold,
          color: tokens.text.primary,
          lineHeight: "160%",
        },
        notchedOutline: {
          borderColor: tokens.border.default,
          borderRadius: tokens.radius.sm,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          "& .MuiTableCell-root": {
            border: `1px solid ${tokens.border.default}`,
            borderTop: 0,
            fontSize: tokens.fontSize.lg,
            lineHeight: "20px",
            fontWeight: tokens.fontWeight.semibold,
            color: tokens.brand.primary,
            backgroundColor: tokens.border.default,
            height: "33px",
            padding: "5px 12px",
            whiteSpace: "nowrap",
          },
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          "& .MuiTableRow-root": {
            "&:nth-of-type(even)": {
              backgroundColor: tokens.bg.hover,
            },
          },
          "& .MuiTableCell-root": {
            color: tokens.text.primary,
            fontSize: tokens.fontSize.lg,
            fontWeight: tokens.fontWeight.semibold,
            lineHeight: "20px",
            letterSpacing: "-0.01em",
            padding: "17px ",
            border: `1px solid ${tokens.border.default}`,
          },
          "& .MuiTableRow-root .MuiTableCell-root:first-of-type": {
            borderLeft: 0,
          },
          "& .MuiTableRow-root .MuiTableCell-root:last-of-type": {
            borderRight: 0,
          },
          "& .MuiTableRow-root:last-of-type .MuiTableCell-root": {
            borderBottom: 0,
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {},
      },
    },

    MuiSelect: {
      styleOverrides: {
        icon: {
          color: tokens.text.muted,
          fontSize: 20,
          marginRight: "8px",
        },
      },
      defaultProps: {
        IconComponent: FiChevronDown,
      },
    },
  },

  palette: {
    neutral: {
      50: "#f7f8f8",
      100: "#e6e7e7",
      200: "#d4d7d7",
      300: "#c0c4c4",
      400: "#aeb3b3",
      500: "#8e9494",
      600: "#687071",
      700: "#424d4d",
      800: "#354141",
      900: "#1c292a",
      950: "#031112",
    },
    grey: {
      100: "#84818A",
    },
    action: {
      active: tokens.text.secondary,
      focus: "rgba(55, 65, 81, 0.12)",
      hover: "rgba(55, 65, 81, 0.04)",
      selected: "#34A6B1",
      disabledBackground: "#F1F2F4",
      disabled: "rgba(55, 65, 81, 0.26)",
    },
    background: {
      default: "#f7f8f8",
      paper: tokens.bg.surface,
    },
    divider: tokens.border.default,
    primary: {
      main: tokens.brand.primary,
      light: tokens.brand.accentLight,
      dark: tokens.brand.primaryHover,
      contrastText: tokens.bg.surface,
    },
    secondary: {
      main: "#172C57",
      light: "#274B95",
      dark: "#122345",
      contrastText: tokens.bg.surface,
    },
    success: {
      main: tokens.status.success,
      light: "#45D985",
      dark: "#1F9854",
      contrastText: "#000000",
    },
    info: {
      main: tokens.status.info,
      light: "#75CDFF",
      dark: "#0FA8FF",
      contrastText: "#000000",
    },
    warning: {
      main: tokens.status.warning,
      light: "#FFCB3D",
      dark: "#D69D00",
      contrastText: "#000000",
    },
    error: {
      main: tokens.status.danger,
      light: "#EE695D",
      dark: "#D02516",
      contrastText: "#000000",
    },
    text: {
      primary: tokens.text.primary,
      secondary: tokens.text.secondary,
      disabled: tokens.text.disabled,
    },
  },
  shape: {
    borderRadius: 6,
  },
  shadows: [
    "none",
    "0px 1px 1px rgba(100, 116, 139, 0.06), 0px 1px 2px rgba(100, 116, 139, 0.1)",
    "0px 1px 2px rgba(100, 116, 139, 0.12)",
    "0px 1px 4px rgba(100, 116, 139, 0.12)",
    "0px 1px 5px rgba(100, 116, 139, 0.12)",
    "0px 1px 6px rgba(100, 116, 139, 0.12)",
    "0px 2px 6px rgba(100, 116, 139, 0.12)",
    "0px 3px 6px rgba(100, 116, 139, 0.12)",
    "0px 2px 4px rgba(31, 41, 55, 0.06), 0px 4px 6px rgba(100, 116, 139, 0.12)",
    "0px 5px 12px rgba(100, 116, 139, 0.12)",
    "0px 5px 14px rgba(100, 116, 139, 0.12)",
    "0px 5px 15px rgba(100, 116, 139, 0.12)",
    "0px 6px 15px rgba(100, 116, 139, 0.12)",
    "0px 7px 15px rgba(100, 116, 139, 0.12)",
    "0px 8px 15px rgba(100, 116, 139, 0.12)",
    "0px 9px 15px rgba(100, 116, 139, 0.12)",
    "0px 10px 15px rgba(100, 116, 139, 0.12)",
    "0px 12px 22px -8px rgba(100, 116, 139, 0.25)",
    "0px 13px 22px -8px rgba(100, 116, 139, 0.25)",
    "0px 14px 24px -8px rgba(100, 116, 139, 0.25)",
    "0px 10px 10px rgba(31, 41, 55, 0.04), 0px 20px 25px rgba(31, 41, 55, 0.1)",
    "0px 25px 50px rgba(100, 116, 139, 0.25)",
    "0px 25px 50px rgba(100, 116, 139, 0.25)",
    "0px 25px 50px rgba(100, 116, 139, 0.25)",
    "0px 25px 50px rgba(100, 116, 139, 0.25)",
  ],
  typography: {
    button: {
      fontWeight: tokens.fontWeight.normal,
      ...fluidTypography.button,
    },
    fontFamily: `"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica,
			 Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji"`,
    body1: {
      ...fluidTypography.body1,
      fontWeight: tokens.fontWeight.normal,
    },
    body2: {
      ...fluidTypography.body2,
      fontWeight: tokens.fontWeight.semibold,
      color: tokens.text.body,
    },
    subtitle1: {
      ...fluidTypography.subtitle1,
      fontWeight: tokens.fontWeight.semibold,
    },
    subtitle2: {
      ...fluidTypography.subtitle2,
      fontWeight: tokens.fontWeight.semibold,
    },
    overline: {
      ...fluidTypography.overline,
      fontWeight: tokens.fontWeight.bold,
    },
    caption: {
      ...fluidTypography.caption,
      fontWeight: tokens.fontWeight.normal,
    },
    h1: {
      ...fluidTypography.h1,
      fontWeight: tokens.fontWeight.heavy,
    },
    h2: {
      ...fluidTypography.h2,
      fontWeight: tokens.fontWeight.heavy,
    },
    h3: {
      ...fluidTypography.h3,
      fontWeight: tokens.fontWeight.bold,
    },
    h4: {
      ...fluidTypography.h4,
      fontWeight: tokens.fontWeight.semibold,
    },
    h5: {
      ...fluidTypography.h5,
      fontWeight: tokens.fontWeight.semibold,
    },
    h6: {
      ...fluidTypography.h6,
      fontWeight: tokens.fontWeight.semibold,
    },
  },
});
